import React, {useCallback, useMemo, useState} from 'react';
import {
    FlatList,
    Platform,
    RefreshControl,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {usePremiumLegacyTheme} from '../helpers/modeScreenTheme';
import {useManagementTheme} from '../helpers/managementTheme';
import {useWorkSheets} from '../helpers/WorkSheetContext';
import {PillTabs} from '../components/premium';
import NewWorkSheetModal from '../components/NewWorkSheetModal';
import {ServiceEmptyState, ServiceLoading} from '../components/ServiceUI';
import {useTopInset} from '../helpers/safeArea';

const STATUS_FILTERS = ['active', 'pending', 'in_progress', 'completed', 'invoiced', 'all'];

// Finished / closed work orders — hidden from the default "active" view.
// completed = kész/finished, invoiced = számlázva/closed. Cancelled/deleted records are
// already dropped upstream in WorkSheetContext (filterActiveWorkOrders).
const CLOSED_STATUSES = new Set(['completed', 'invoiced']);

// Parse a work order's creation time for newest-first sorting; unknown dates sink to the bottom.
const getCreatedTime = (ws) => {
    const time = Date.parse(ws?.createdAt);
    return Number.isNaN(time) ? 0 : time;
};

/**
 * Work-sheet status as a tinted pill (tone colour on a 14 % tint).
 *
 * @param {object} props
 * @param {string} props.status `pending` | `in_progress` | `completed` | `invoiced`.
 * @param {Function} props.t i18n translate.
 * @returns {JSX.Element} The pill.
 */
const StatusBadge = ({ status, t }) => {
    const m = useManagementTheme();
    const config = {
        pending: { label: t('repair_status_pending'), tone: 'warning' },
        in_progress: { label: t('repair_status_in_progress'), tone: 'info' },
        completed: { label: t('repair_status_completed'), tone: 'success' },
        invoiced: { label: t('repair_status_invoiced'), tone: 'violet' },
    };
    const c = config[status] || config.pending;
    return (
        <View style={[styles.statusBadge, { backgroundColor: m.toneTint(c.tone, 0.14) }]}>
            <Text style={[styles.statusBadgeText, { color: m.toneColor(c.tone) }]}>{c.label}</Text>
        </View>
    );
};

const WorkSheetListScreen = () => {
    const { t } = useTranslation();
    const { theme } = usePremiumLegacyTheme();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const { worksheets, loaded, syncing, errors, fetchWorksheets } = useWorkSheets();
    const topInset = useTopInset();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('active');
    const [isNewModalVisible, setIsNewModalVisible] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await fetchWorksheets?.();
        } finally {
            setRefreshing(false);
        }
    }, [fetchWorksheets]);

    const allSheets = worksheets;


    const filteredSheets = useMemo(() => {
        let result = allSheets;
        if (activeFilter === 'active') {
            // Default view: hide finished (completed) and closed (invoiced) work orders;
            // cancelled/deleted are already excluded upstream in WorkSheetContext.
            result = result.filter(ws => !CLOSED_STATUSES.has(ws.status));
        } else if (activeFilter !== 'all') {
            result = result.filter(ws => ws.status === activeFilter);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter(ws =>
                (ws.customer || '').toLowerCase().includes(q) ||
                (ws.plate || '').toLowerCase().includes(q) ||
                (ws.worksheetId || '').toLowerCase().includes(q) ||
                (ws.car || '').toLowerCase().includes(q)
            );
        }
        // Newest first: most recently created work order on top, oldest at the bottom.
        return [...result].sort((a, b) => getCreatedTime(b) - getCreatedTime(a));
    }, [searchQuery, activeFilter, allSheets]);

    const getFilterLabel = useCallback((filter) => {
        const labels = {
            all: t('repair_filter_all'),
            active: t('repair_filter_active'),
            pending: t('repair_status_pending'),
            in_progress: t('repair_status_in_progress'),
            completed: t('repair_status_completed'),
            invoiced: t('repair_status_invoiced'),
        };
        return labels[filter] || filter;
    }, [t]);

    const getFilterCount = useCallback((filter) => {
        if (filter === 'all') return allSheets.length;
        if (filter === 'active') return allSheets.filter(ws => !CLOSED_STATUSES.has(ws.status)).length;
        return allSheets.filter(ws => ws.status === filter).length;
    }, [allSheets]);

    const handleSheetPress = useCallback((worksheet) => {
        navigation.navigate('WorkSheetDetailScreen', { worksheet });
    }, [navigation]);

    const handleSaveNewWorksheet = useCallback(async (worksheetData) => {
        // The modal already persisted the work order via the composite API.
        // Just navigate to the detail screen with the returned data.
        setIsNewModalVisible(false);
        if (worksheetData) {
            navigation.navigate('WorkSheetDetailScreen', {
                isNew: false,
                worksheet: worksheetData,
            });
        }
    }, [navigation]);

    // Stable keyExtractor identity (byte-identical key: item._id), matching the
    // other blueprint list screens so it isn't reallocated on every render.
    const keyExtractor = useCallback((item) => item._id, []);

    const renderWorkSheet = useCallback(({ item }) => {
        const partsList = Array.isArray(item.parts) ? item.parts : [];
        const tasksList = Array.isArray(item.tasks) ? item.tasks : [];
        const totalParts = partsList.reduce((sum, p) => sum + ((p?.qty || 0) * (p?.unitPrice || 0)), 0);
        const completedTasks = tasksList.filter(t => t.status === 'completed').length;

        return (
            <TouchableOpacity
                style={[styles.wsCard, {
                    backgroundColor: theme.colors.card,
                    borderColor: theme.colors.border,
                }, m.shadow(1)]}
                onPress={() => handleSheetPress(item)}
                activeOpacity={0.8}
            >
                <View style={styles.wsCardHeader}>
                    <View style={styles.wsIdContainer}>
                        <Icon name="file-document-outline" size={16} color={theme.colors.primary} />
                        <Text style={[styles.wsId, { color: theme.colors.primary }]}>{item.worksheetId}</Text>
                    </View>
                    <StatusBadge status={item.status} t={t} />
                </View>

                <View style={styles.wsCardBody}>
                    <Text style={[styles.wsCustomer, { color: theme.colors.text }]}>{item.customer}</Text>
                    <View style={styles.wsCarRow}>
                        <Icon name="car" size={16} color={theme.colors.subText} />
                        <Text style={[styles.wsCarText, { color: theme.colors.subText }]}>{item.car} ({item.year})</Text>
                        <View style={[styles.wsPlateBadge, {
                            backgroundColor: m.colors.surfaceAlt,
                            borderColor: theme.colors.border,
                        }]}>
                            <Text style={[styles.wsPlateText, { color: theme.colors.text }]}>{item.plate}</Text>
                        </View>
                    </View>
                    <Text style={[styles.wsJobText, { color: theme.colors.subText }]} numberOfLines={1}>
                        <Icon name="wrench" size={13} color={theme.colors.subText} /> {item.jobSummary}
                    </Text>
                </View>

                <View style={[styles.wsCardFooter, { borderTopColor: theme.colors.border }]}>
                    <View style={styles.wsFooterItem}>
                        <Icon name="clock-outline" size={14} color={theme.colors.subText} />
                        <Text style={[styles.wsFooterText, { color: theme.colors.subText }]}>{item.estimatedHours}h</Text>
                    </View>
                    <View style={styles.wsFooterItem}>
                        <Icon name="check-circle-outline" size={14} color={theme.colors.subText} />
                        <Text style={[styles.wsFooterText, { color: theme.colors.subText }]}>
                            {completedTasks}/{tasksList.length} {t('repair_tasks_short')}
                        </Text>
                    </View>
                    <View style={styles.wsFooterItem}>
                        <Icon name="package-variant" size={14} color={theme.colors.subText} />
                        <Text style={[styles.wsFooterText, { color: theme.colors.subText }]}>{partsList.length} {t('repair_parts_short')}</Text>
                    </View>
                    {totalParts > 0 && (
                        <Text style={[styles.wsTotalText, { color: theme.colors.success }]}>
                            {totalParts.toLocaleString('hu-HU')} Ft
                        </Text>
                    )}
                </View>
            </TouchableOpacity>
        );
    }, [handleSheetPress, t, theme, m]);

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            {/* Header */}
            <View style={[styles.header, {paddingTop: topInset + 12}]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={{
                        width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center',
                        backgroundColor: theme.colors.card, borderWidth: 1, borderColor: theme.colors.border,
                    }}>
                        <Icon name="chevron-left" size={26} color={theme.colors.text} />
                    </TouchableOpacity>
                    <Text style={[styles.headerTitle, { color: theme.colors.text }]}>{t('repair_worksheets')}</Text>
                </View>
                <TouchableOpacity
                    style={[styles.newButton, { backgroundColor: theme.colors.primary }]}
                    onPress={() => setIsNewModalVisible(true)}
                    activeOpacity={0.7}
                >
                    <Icon name="plus" size={20} color="#fff" />
                    <Text style={styles.newButtonText}>{t('repair_new')}</Text>
                </TouchableOpacity>
            </View>

            {/* Search */}
            <View style={[styles.searchContainer, {
                backgroundColor: theme.colors.card,
                borderColor: theme.colors.border,
            }]}>
                <Icon name="magnify" size={20} color={theme.colors.subText} style={styles.searchIcon} />
                <TextInput
                    style={[styles.searchInput, { color: theme.colors.text }]}
                    placeholder={t('repair_search_placeholder')}
                    placeholderTextColor={theme.colors.placeholder}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                />
                {searchQuery.length > 0 && (
                    <TouchableOpacity onPress={() => setSearchQuery('')}>
                        <Icon name="close-circle" size={18} color={theme.colors.subText} />
                    </TouchableOpacity>
                )}
            </View>

            {/* Status Filters */}
            <View style={styles.filtersSection}>
                <PillTabs
                    scroll
                    variant="solid"
                    options={STATUS_FILTERS.map((filter) => ({ value: filter, label: `${getFilterLabel(filter)} (${getFilterCount(filter)})` }))}
                    value={activeFilter}
                    onChange={setActiveFilter}
                />
            </View>

            {/* Work Sheet List */}
            {!loaded && allSheets.length === 0 ? (
                <ServiceLoading theme={theme} label={t('repair_loading')} />
            ) : (
                <FlatList
                    data={filteredSheets}
                    renderItem={renderWorkSheet}
                    keyExtractor={keyExtractor}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                    initialNumToRender={10}
                    maxToRenderPerBatch={8}
                    windowSize={5}
                    removeClippedSubviews={Platform.OS === 'android'}
                    refreshControl={
                        <RefreshControl
                            refreshing={refreshing || syncing}
                            onRefresh={onRefresh}
                            tintColor={theme.colors.primary}
                            colors={[theme.colors.primary]}
                        />
                    }
                    ListEmptyComponent={
                        <ServiceEmptyState
                            theme={theme}
                            icon="file-search-outline"
                            title={t('repair_no_worksheets')}
                            subtitle={errors?.worksheets || t('repair_add_new_worksheet') || ''}
                            actionLabel={t('repair_new')}
                            onAction={() => setIsNewModalVisible(true)}
                        />
                    }
                />
            )}

            <NewWorkSheetModal
                visible={isNewModalVisible}
                onClose={() => setIsNewModalVisible(false)}
                onSave={handleSaveNewWorksheet}
                theme={theme}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        // paddingTop applied dynamically via useTopInset()
        paddingBottom: 12,
    },
    headerTitle: {
        fontSize: 28,
        fontWeight: '800',
        letterSpacing: -0.6,
    },
    newButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 999,
        gap: 6,
    },
    newButtonText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 14,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 999,
        marginHorizontal: 16,
        paddingHorizontal: 16,
        marginBottom: 12,
        borderWidth: 1,
    },
    searchIcon: {
        marginRight: 8,
    },
    searchInput: {
        flex: 1,
        height: 44,
        fontSize: 15,
    },
    filtersSection: {
        zIndex: 10,
        marginBottom: 12,
        paddingHorizontal: 12,
    },
    listContent: {
        paddingHorizontal: 16,
        paddingTop: 2,
        paddingBottom: 80,
    },
    wsCard: {
        borderRadius: 24,
        padding: 16,
        marginBottom: 12,
        borderWidth: 1,
    },
    wsCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    wsIdContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    wsId: {
        fontSize: 13,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    statusBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 20,
    },
    statusBadgeText: {
        fontSize: 11,
        fontWeight: '700',
        textTransform: 'uppercase',
    },
    wsCardBody: {
        marginBottom: 12,
    },
    wsCustomer: {
        fontSize: 17,
        fontWeight: '600',
        marginBottom: 6,
    },
    wsCarRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 4,
    },
    wsCarText: {
        fontSize: 13,
    },
    wsPlateBadge: {
        paddingHorizontal: 10,
        paddingVertical: 2,
        borderRadius: 999,
        borderWidth: 1,
    },
    wsPlateText: {
        fontSize: 12,
        fontWeight: '700',
        letterSpacing: 1,
    },
    wsJobText: {
        fontSize: 13,
        marginTop: 4,
    },
    wsCardFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        paddingTop: 10,
        borderTopWidth: 1,
    },
    wsFooterItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    wsFooterText: {
        fontSize: 12,
        fontWeight: '500',
    },
    wsTotalText: {
        fontSize: 13,
        fontWeight: '700',
        marginLeft: 'auto',
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 60,
        gap: 12,
    },
    emptyText: {
        fontSize: 15,
        fontWeight: '500',
    },
});

export default WorkSheetListScreen;
